<template>
<div class="chapter">
<div class="inner_page">
<div class="chapter" title="Quick Wits and Canny Minds"><div class="titlepage"><div><div><h1 class="title"><a id="id1516724">Quick Wits and Canny Minds</a></h1></div></div></div><a id="id1516724">
<div class="blockquote"><table border="0" cellpadding="0" cellspacing="0" class="blockquote" summary="Block quote" width="100%"><tbody><tr><td valign="top"> </td><td valign="top"><p>“I am thankful for laughter, except for when milk comes out of my nose.” </p></td><td valign="top"> </td></tr><tr><td valign="top"> </td><td align="right" colspan="2" valign="top">--<span class="attribution">Woody Allen</span></td></tr></tbody></table></div>
<p>A grandmother is watching her grandson playing on the beach at Coney Island. Suddenly, a
        huge wave crashes onto the shore and washes the boy out to sea. </p>
<p>The grandmother lifts her hands to the sky and pleads, “Please God, save my only grandson.
        Please, I beg of you, bring him back to me!” </p>
<p>Astoundingly, another huge wave rolls in and washes the boy back onto the beach, as good
        as new. His grandmother looks back up to the heavens and cries, “He had a hat!”</p>
</a><p><a id="id1516724">That’s one of my favorite jokes by comic Myron Cohen (1902–1986). My guess is that Myron
        was a pretty bright guy. After all, he wrote his own material, and having a sense of humor
        is an important part of being brilliant.<sup>[</sup></a><sup class="footnote-out"><a class="footnote" href="#/#ftn.id1516772" id="id1516772">2</a></sup>]</p>
<p>Don’t believe it? Abraham Lincoln had the best sense of humor of any American president,
        and his brilliance is unquestioned. (So don’t question it!) For example, he once watched as
        a woman wearing a plumed hat slipped and fell on her backside into a puddle. Before offering
        his assistance, Lincoln remarked to his companion, “Reminds me of a duck. Feathers on her
        head, down behind.”</p>
<div class="sidebar" title="How to Tell a Joke"><p class="title"><b>How to Tell a Joke</b></p>
<p>1. Approach slowly, showing your hands so it’s clear no harm is intended.</p>
<p>2. Slyly determine the person’s political opinions, e.g., “How about that Supreme
            Court, huh?” </p>
<p>3. Use the information you gather to amuse the person without insulting or pandering
            to them. </p>
<p>4. Watch for inappropriate signs. These include grimaces, muttering, and the hurling
            of diesel fuel and/or wheat grass juice.</p>
</div>
<p>Still not convinced there’s a connection? Well, the <span class="emphasis"><em>Journal of General
            Psychology</em></span> published a study that found “a positive relationship between
        intelligence and joke comprehension.” See, I have evidence. (Don’t get used to it,
        though.)</p>
<p>As missionaries and traders, French travelers to North America had less interest than
        other Europeans in stealing Indian lands. Further, the French often chose to learn about and
        live among Indians, which led to friendships, alliances, and humor.</p>
<p>For example, Indians are relatively hairless, and they found the notion of a beard to be
        completely <span class="emphasis"><em>freakish.</em></span> That’s why one Huron man stared into a Frenchman’s
        bearded face with extraordinary attention for a long time.</p>
<p>The Huron finally broke his silence, exclaiming, “Oh, the bearded man! Oh, how ugly he
        is!” French settlers seized on this Huron horror of the hirsute, and told the Huron that
        European <span class="emphasis"><em>women</em></span> had beards too.</p>
<p>You can imagine the Huron’s reaction to that news.</p>
<p>Even a topic as boring as the weather could be a source of intercultural laughter. In
        1728, a Virginian named William Byrd wrote about a conversation he overheard between an
        Englishman and an Indian. The Indian asked what caused thunder during rainstorms.</p>
<p>The Englishman replied that the god of the English was shooting his gun at the gods of the
        Indians. </p>
<p>Playing along, the Indian observed that his gods must be frightened by this, as they could
        then not “hold their water.”</p>
<p>As for the Puritans, they were not a barrel of laughs. If a Puritan slipped on a banana
        peel, rather than making a joke, he and everyone else would wonder what he had done to
        deserve God’s punishment. (“This banana peel doth reek of brimstone!”)</p>
<p>But Puritans and other religious immigrants were often businesspeople. So the idea of a
        religious person who would never cheat (unless it was to his or her advantage!) got a lot of
        mileage in early America.</p>
<div class="mediaobject" title="This is an illustration of a banana peel."><img alt="How to Tell a Joke" src="images/6_banana-peel-3.png"/></div>
<p>For example, there’s the one about a pious couple who ran a grocery store in
        Massachusetts. Before settling down in the evening, the wife asked her husband, “Have you
        added sand to the sugar? Put lard in the butter? Tossed some flour in with the ginger?” she
        asked. </p>
<p>“Yes, dear,” her husband responded.</p>
<p>“Then come in for prayers.”</p>
<div class="sidebar" title="A Hobgoblin Resideth in My Colon"><p class="title"><b>A Hobgoblin Resideth in My Colon</b></p>
<p>American colonists ate a lot of pork, cabbage, and grain. In short, they had bad gas,
            as William Byrd (1674–1744) pointed out in his poem “Upon a Fart”:</p>
<p>Gentlest blast of ill concoction, </p>
<p>The reverse of the ascending belch;</p>
<p>The only stink avoided by Scotsman,</p>
<p>Beloved and practiced by the Welsh.</p>
<p>Softest note of inward griping,</p>
<p>A reverence’s finest part,</p>
<p>So fine it needs no wiping,</p>
<p>Except if it’s a brewer’s fart.</p>
<p>Swiftest ease of inner pain,</p>
<p>Vapor from a secret stench,</p>
<p>It’s rattled out by the unbred swain<sup>[<sup class="footnote-out"><a class="footnote" href="#/#ftn.id1516998" id="id1516998">3</a></sup>]</sup></p>
<p>But whispered by the bashful wench.</p>
<p>Shapeless fart! We never can show you</p>
<p>But in that merry sport</p>
<p>By which, by burning, we know you</p>
<p>To the amazement of all in court.</p>
</div>
<h2><a id="id1516939">Yankee Doodle</a></h2><a id="id1516939">
<p>Civilized folk (like the British) had the impression that colonial Americans were hicks,
        which brings us to the Yankee Doodles. The music for what became “Yankee Doodle” was
        composed around 1755 by a British Army surgeon. The tune is the same as the one you know,
        but the original words were different: </p>
<p><span class="strong"><strong>Dolly Bushel let a Fart</strong></span></p>
<p><span class="strong"><strong>Jenny Jones she found it</strong></span></p>
<p><span class="strong"><strong>Ambrose carried it to the Mill</strong></span></p>
<p><span class="strong"><strong>Where Doctor Warren ground it.</strong></span></p>
<p>About twenty years later, the term “Yankee Doodle” came to mean any numbskull living in
        New England. Soon, the tune sported new lyrics about a Yankee Doodle, and it became a
        favorite marching song of the British Army. But after the Battle of Bunker Hill, the rebels
        began playing it themselves, and by 1776, the “macaroni” lyrics to the tune appeared on the
        scene. Why macaroni? There are some mysteries in life that will never be explained.</p>
<p>“Frontier humor” also dominated the American landscape, with countless jokes about people
        moving to where they thought there would be rivers of milk and honey. (This would be pretty
        disgusting: “Ma, I caught a buttermilk trout!”) </p>
<p>As territories became states, the ribbing of particular states became common, as in, “What
        do divorces and hurricanes in West Virginia have in common? Either way, someone’s going to
        lose a trailer.”</p>
<div class="mediaobject" title="This is an illustration of a trailer."><img alt="A Hobgoblin Resideth in My Colon" src="images/7_mobile-home-3.png"/></div>
</a><h2><a id="id1516939"></a><a id="id1517112">Picture Perfect</a></h2><a id="id1517112">
<p>Zebulon Vance was the Confederate governor of North Carolina. After the Civil War, he was
        imprisoned for a short time, received a pardon, and was later elected to the Senate. But his
        Yankee colleagues enjoyed joking with him about his past. While visiting in Massachusetts,
        Vance reportedly went into an outhouse where a picture of Confederate general Robert E. Lee
        was hanging. Upon Vance’s return, the men asked if he’d seen the bathroom portrait. He told
        them its location made sense, because “If ever a man lived who could scare the dung out of
        the Yankees, that man was Robert E. Lee.”</p>
</a><p><a id="id1517112">Foreigners have noted that American humor has a strong element of pain, cruelty, and death
        in it. Do you remember the “dead babies” jokes of the 1960s? (Neither do I!)<sup>[</sup></a><sup class="footnote-out"><a class="footnote" href="#/#ftn.id1517128" id="id1517128">4</a></sup>] And let’s not pretend this is relegated to the distant past. My own neighbor
        told me a joke that qualifies as the worst knock-knock joke in history: </p>
<p><span class="strong"><strong>Knock, knock. </strong></span></p>
<p><span class="strong"><strong>Who’s there? </strong></span></p>
<p><span class="strong"><strong>9/11. </strong></span></p>
<p><span class="strong"><strong>9/11 who? </strong></span></p>
<p><span class="strong"><strong>You said you’d never forget!</strong></span></p>
<p>Along the lines of unlikely subjects for humor, after Apache war chief Geronimo
        surrendered to the United States in 1886, he lived the rest of his life as a prisoner of
        war. Even so, Geronimo was granted some remarkable freedoms.</p>
<p>General Leonard Woods related how Geronimo once asked to inspect the general’s rifle.
        Looking it over, Geronimo then asked for some bullets. A little anxious, the general
        courteously showed him how to load and fire the gun.</p>
<p>Geronimo fired at a target and nearly hit a man passing by. As Woods wrote, Geronimo
        “regarded [this] as a great joke, rolling on the ground and laughing heartily, ‘Good
        gun!’”</p>
<p>In 1876, writer S. S. Cox analyzed some of the unique traits of American humor. He noted
        that Americans were known for putting their feet up on furniture, spitting tobacco, and
        constantly exaggerating. Cox described the traveling American as someone who “leaves his
        trail on every mountain-pass, his axe-stroke in every forest.” </p>
<div class="sidebar" title="Infuriation Nation"><p class="title"><b>Infuriation Nation</b></p>
<p>Research who a person’s least-favorite historical figure is. Does your neighbor start
            angrily squintin’ when you mention Bill Clinton? Is your Uncle Stu upset about Spiro
            Agnew? Know any piano movers who get steamed by Herbert Hoover?</p>
<p>Get a small framed picture of that not-so-special someone and sneak it onto your
            target’s mantle, desk, or bedside . . . Anywhere with enough clutter that the photo
            won’t be spotted immediately. If you’re patient, you’ll have the last laugh! (And
            somewhere, Herbert Hoover will be smiling too.)</p>
</div>
<p>For African Americans, humor has historically been a way to cope with a society that
        denied them their rights. As Frederick Douglass wrote, when he found himself in unfriendly
        places, he was greatly helped by his “tendency to see the funny side of things.” </p>
<p>The black humor tradition often makes fun of injustices and has an unsurprising dollop of
        anger and frustration. For example, black writer Paul Beatty once observed that one of the
        funniest things he ever heard was an interview with football star Jim Brown. The ex-athlete
        was getting angrier and angrier about the questions he was being asked. </p>
<p>When the interviewer suggested Brown take an anger management class, Brown answered, “What
        are you talking about, man? I <span class="emphasis"><em>teach</em></span> anger management!” (And he did,
        too.)</p>
<div class="sidebar" title="Good Point!"><p class="title"><b>Good Point!</b></p>
<p>In the 1800s, a group of religious leaders lobbied in favor of expanding the United
            States. Their argument was that Manifest Destiny was on their side because the United
            States is a Christian country.</p>
<p>“So is hell,” Mark Twain responded.</p>
</div>
<p><span class="emphasis"><em>The Pocket Guide to Mischief</em></span> describes the Scottish tradition of
        flyting, which was an insult contest designed to show off a person’s deadly wit. Because
        slaves were rarely taught to read and write, verbal skills and storytelling were emphasized
        in early black culture. This helps to explain “the Dozens,” the practice of verbally dissing
        an opponent by saying cruel things about his or her mother and other close relatives. For
        groups like the Puritans, comparing someone’s mom to an unpleasant set of freakish
        circumstances would have resulted in BIG trouble.</p>
<p><span class="strong"><strong>Some modern examples of the Dozens include: </strong></span></p>
<p><span class="strong"><strong>Yo mama’s so big, her oatmeal bowl comes with a
            lifeguard.</strong></span></p>
<p><span class="strong"><strong>Yo mama’s so fat, she’s got smaller fat women orbiting around
            her.</strong></span></p>
<p><span class="strong"><strong>Yo mama’s so ugly, when she joined an ugly contest, they said,
            “Sorry, no professionals.”</strong></span></p>
<p><span class="strong"><strong>Yo mama eats so much, when she goes to an all-you-can-eat buffet,
            they have to install speed bumps.</strong></span></p>
<p>You get the idea. Writer H. Rap Brown has pointed out that the practice of “signifying” is
        a more humane practice than the Dozens. In signifying, you can rip on your opponent (instead
        of his or her mother). For example: “If I had your face, I’d give my head away.”</p>
<p>If you’re signifying, you can also make yourself sound great: “I might not be the best in
        the world, but I’m in the top two, and my brother’s getting old.”</p>
<p>It’s not hard to trace a line from boasting to signifying to the rap and hip-hop music
        movements. You know, like “I’m smooth and wise, the skills I utilize / Lyrics all advanced,
        you’d think my brain was computerized” (Lord Finesse, 1992). That’s much more impressive
        than rapper T. I.: “T. I., they call me ‘Candle Guy’ / Probably cuz I am on fire.” Or the
        guys in Clipse, who rap that they’re “the black Martha Stewart / Let me show you how to do
        it.” But even Clipse boasts better than Mase: “Young, black and famous / with money hangin’
        out the anus.” </p>
<p>Ahem.</p>
<div class="sidebar" title="Texas: It’s Like a Whole Other Country"><p class="title"><b>Texas: It’s Like a Whole Other Country</b></p>
<p>There are fifty states in the United States, and each of them has a motto. In a
            pretentious flourish, many of these mottoes are in foreign languages like Greek, Latin,
            and Hawaiian. With the exception of New Hampshire (“Live free or die”), most mottoes are
            not particularly inspirational or even accurate.</p>
<p>Folks living in rival states need to be deflated at every opportunity, and here’s one
            now! Learn the motto of a state that borders yours. (If you are outside the United
            States, go ahead and pick California.) Now alter the motto slightly or entirely. For
            example, New Hampshire’s new motto might be “Live free or pie,” “Live free or don’t,” or
            “Live free or my my, this is funny.”</p>
<p>In addition to official mottoes, all states have at least one slogan. These slogans
            are intended to promote tourism. Thus, we have Wisconsin’s slogan, “Stay Just a Little
            Bit Longer.” (A little needy, don’t you think?) And Missouri has pulled in valuable
            spelunking dollars by declaring itself “The Cave State.”</p>
<p>Riff on existing state slogans or just make up a brand new one. A time-honored example
            of this would be “Arizona: It’s Dehyd-rific!” </p>
<p>Other possibilities:</p>
<p>“New Jersey: Non-Hypoallergenic—Come See for Yourself!”</p>
<p>“Pennsylvania: Baking Crumpets with Clean Coal Technology Since Yesterday!”</p>
<p>“Washington: There’s Nothing to Do Here After Ten AM!”</p>
</div>
<p>United States citizens who constantly refer to their cash and possessions are following in
        the steps of an old American tradition. Yes, stupidity, but also being fixated on MONEY. In
        the 1830s, visiting Frenchman Alexis de Tocqueville observed, “I know of no country, indeed,
        where the love of money has taken a stronger hold on the affections of men.” American writer
        Washington Irving agreed when he assessed his fellow citizens and decided that the “almighty
        dollar [was the] great object of universal devotion.”</p>
<p>Jews have made an outsized contribution to American humor. In 1982, a <span class="emphasis"><em>New York
            Times</em></span> reporter estimated that about 75 percent of stand-up comedians in the
        United States were Jewish, although the Jewish percentage of the general population was
        between 2 and 3 percent of the total population! </p>
<p>Historically, Jewish humor has often played off being a minority in a non-Jewish
        (“Gentile”) population. Jewish jokes have often included Gentiles suspicious of the
        education and business skills of Jews. These jokes are unique in that they often make fun of
        Jews themselves (as opposed to another group). This is funny and also a subtle way of
        saying, “If we can laugh at ourselves, why should you be prejudiced toward us?” </p>
<p>In 1870, New York’s first elevated railway opened in Manhattan. It had been running for
        about a month when, one day, a big Gentile got on a car and took the only available seat. He
        looked over and saw that he was next to a Jewish woman.</p>
<p>“Excuse me, but I’ve always wondered,” the man said. “Why are Jews so much cleverer than
        everyone else?” </p>
<p>The woman thought for a moment as the El rattled along, and finally said, “It might be
        because of all the herring we eat.” At that, the woman took a piece of herring out of a bag
        and began to eat it. </p>
<p>“How much do you want for the rest of the herring in that bag?” the man asked.</p>
<p>“Ten dollars,” she replied. The transaction was made, and the man began eating a herring.
        Then he stopped mid-bite. “This is crazy,” he said. “I could have bought all this herring
        for much cheaper at the next stop!” </p>
<p>“You see,” said the woman. “It’s working already!”</p>
<div class="sidebar" title="United States Foreign Policy Explained"><p class="title"><b>United States Foreign Policy Explained</b></p>
<p>Three Boy Scouts reported their good deed of the day to their scoutmaster. “We helped
            an old lady across the street,” said one. </p>
<p>“Okay,” said the scoutmaster. “But why do all three of you take credit for the good
            deed?”</p>
<p>“Well,” the Scouts explained, “she didn’t want to go.”</p>
</div>
<h2><a id="id1517501">Making a Difference</a></h2><a id="id1517501">
<p>Your local politicians work hard! To let them know you appreciate it, buy a large number
        of postcards. Every Monday, write a clean, tasteful joke on the back of one of the cards.
        Then mail it to your representative, senator, or other local official. Continue this
        practice for his or her full term of office. The ray of sunshine you’ll bring into this
        politician’s life may inspire him or her to initiate some useful legislation. Plus, who
        knows? Maybe you’ll be offered a good job as a speechwriter (or perhaps there’s an opening
        in the mail room!).</p>
</a><div class="footnotes"><a id="id1517501"><br/><hr/></a><div class="footnote"><a id="id1517501">
</a><p><a id="id1517501"><sup>[</sup></a><a class="para" href="#/#id1516772" id="ftn.id1516772">2</a>] 1. Never mind that a genius named Isaac Newton supposedly laughed only once in his
                life.</p>
</div><div class="footnote">
<p><sup>[<a class="para" href="#/#id1516998" id="ftn.id1516998">3</a>] </sup>2. A country youth.</p>
</div><div class="footnote">
<p><sup>[<a class="para" href="#/#id1517128" id="ftn.id1517128">4</a>] </sup>3. Example: Q: What's red and swings? A: A baby on a meat hook.</p>
</div></div></div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// ch02.vue?1138bfc2